<template>
  <v-container fluid class="pa-4">
    <!-- Overlay de loading -->
    <v-overlay v-model="isLoading" :z-index="99999" class="d-flex align-center justify-center">
      <v-progress-circular indeterminate size="64" color="primary" />
      <span class="ml-4" style="font-size:1.3em">{{ loadingLabel }}</span>
    </v-overlay>

    <v-row class="ma-0">
      <ReportFilterDate @rangeUpdated="handleRangeUpdated" style="width:calc(100% - 70px);" />
      

      <span class="d-inline-block">
        <!-- El activador del tooltip es el padre (este <span>), así funciona aunque el botón esté disabled -->
        <v-tooltip
          activator="parent"
          location="bottom"
          :disabled="!filter[0]==''"
        >
          <span>Selecciona un rango de fechas para exportar</span>
        </v-tooltip>

        <v-menu open-on-hover :disabled="isLoading || filter[0]==''">
          <template #activator="{ props: menuProps }">
            <!-- Envolvemos el botón en un span para que el hover abra el tooltip aunque el botón esté disabled -->
            <span v-bind="menuProps">
              <v-btn
                icon
                size="small"
                class="mt-lg-2 mx-3"
                variant="text"
                :disabled="isLoading || filter[0]==''"
                aria-label="Descargar reporte"
              >
                <v-icon>mdi-download</v-icon>
              </v-btn>
            </span>
          </template>

          <v-list>
            <v-list-item @click="exportToPDF">Exportar PDF</v-list-item>
            <v-list-item @click="exportToExcel">Exportar Excel</v-list-item>
          </v-list>
        </v-menu>
      </span>

    </v-row>

    <!-- Tu contenido visual del reporte se mantiene igual -->
    <div>
      <v-container class="pa-0">
        <!-- Sección 1: Ventas -->
        <div>
          <SubIvaTotalVue :cols="4" :records="salesTotalsData" />
          <br />
          <v-row class="ma-0">
            <v-col md="8"  class="px-0 px-lg-3">
              <SoldByUser :data="sold_by_user" />
            </v-col>
            <v-col md="4"  class="px-0 px-lg-3">
              <SalesByMedium :data="sold_by_medium"/>
            </v-col>
          </v-row>
          <br />
          <SalesByProducts v-if="IS_DOMINIOMEDIOS" :data="sold_by_product"/>
        </div>

        <!-- Sección 2: Cotizaciones -->
        <div>
          <v-row class="ma-0">
            <v-col md="7" class="px-0 px-lg-3">
              <QuotedByUser :data="quoted_by_user" />
            </v-col>
            <v-col md="5" class="px-0 px-lg-3">
              <SubIvaTotalVue class="pt-4" :cols="2" :records="quotationsTotalsData" />
            </v-col>
          </v-row>
          <v-row class="ma-0">
            <v-col md="4" class="px-0 px-lg-3">
              <ProspectsVsClients :style="isDarkMode?'background:#212121; border-radius: 4px; padding:20px 0px;':'margin-top:20px;'" :data="prospectsVsClientsData"/>
            </v-col>
            <v-col md="8" class="px-0 px-lg-3">
              <ProspectsByStatus :data="prospectsByStatusData" />
            </v-col>
          </v-row>
        </div>
      </v-container>
    </div>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useSalesStore } from '@/store/sales';
import { useQuotationsStore } from '@/store/quotations';
import { useReportsStore } from '@/store/reports'; // 👈 nuevo store

import SubIvaTotalVue from '@/components/reports/cards/SubIvaTotal.vue';
import ReportFilterDate from '@/components/filters/date/Container.vue'
import ProspectsVsClients from '@/components/reports/charts/QuotedProspectsVsClients.vue'
import ProspectsByStatus from '@/components/reports/charts/ProspectsByStatus.vue'
import SoldByUser from '@/components/reports/charts/SoldByUser.vue'
import QuotedByUser from  '@/components/reports/charts/QuotedByUser.vue'
import SalesByMedium from  '@/components/reports/charts/SalesByMedium.vue'
import SalesByProducts from  '@/components/reports/charts/SalesByProducts.vue'

// Stores
const salesStore = useSalesStore();
const quotationsStore = useQuotationsStore();
const reportsStore = useReportsStore();

// Datos actuales de tus stores
const salesTotalsData = computed(() => salesStore.sales_totals);
const quotationsTotalsData = computed(() => {
  const raw = quotationsStore.quotations_totals;
  if (Array.isArray(raw)) return raw[0] || null;
  return raw;
});
const prospectsVsClientsData = computed(() => quotationsTotalsData.value?.totals_by_subject_type ?? {});
const prospectsByStatusData = computed(() => quotationsTotalsData.value?.sums_by_opportunity_status ?? {});
const sold_by_user = computed(() => salesStore.sold_by_user);
const quoted_by_user = computed(() => quotationsStore.quoted_by_user);
const sold_by_medium = computed(() => salesStore.sold_by_medium);
const sold_by_product = computed(() => salesStore.sold_by_product);

// Filtro de fechas
const filter = ref<[string, string]>(['', '']);
const handleRangeUpdated = async (range: { start: Date, end: Date } | null) => {
  let params = {};
  if (range) {
    const from = range.start.toLocaleString("sv-SE", { timeZone: "America/Monterrey" }).slice(0, 10);
    const to = range.end.toLocaleString("sv-SE", { timeZone: "America/Monterrey" }).slice(0, 10);
    if (filter.value[0] !== from || filter.value[1] !== to) {
      filter.value = [from, to];
      params = { from, to };
    } else {
      return;
    }
  } else {
    filter.value = ['', ''];
  }
  await Promise.all([
    salesStore.soldByUser(params),
    salesStore.soldByMedium(params),
    salesStore.soldByProduct(params),
    salesStore.salesTotals(params),
    quotationsStore.quotedByUser(params),
    quotationsStore.quotationsTotals(params)
  ]);
};

// Modo oscuro (ya lo tienes)
const isDarkMode = ref(window.matchMedia('(prefers-color-scheme: dark)').matches);
window.matchMedia('(prefers-color-scheme: dark)')
  .addEventListener('change', e => isDarkMode.value = e.matches);

// Loading states
const loadingPDF = ref(false);
const loadingExcel = ref(false);
const isLoading = computed(() => loadingPDF.value || loadingExcel.value);
const loadingLabel = computed(() => loadingPDF.value ? 'Exportando PDF...' : loadingExcel.value ? 'Exportando Excel...' : '');

// Parámetros para export
const scope = ref<'team_and_all_sellers' | 'team_only' | 'me'>('team_and_all_sellers'); // ajusta si tienes UI para esto
const exportParams = computed(() => ({
  from: filter.value[0] || undefined,
  to: filter.value[1] || undefined,
  scope: scope.value
}));

// Exportar vía backend
const exportToPDF = async () => {
  loadingPDF.value = true;
  try {
    await reportsStore.exportPDF(exportParams.value);
  } finally {
    loadingPDF.value = false;
  }
};

const exportToExcel = async () => {
  loadingExcel.value = true;
  try {
    await reportsStore.exportExcel(exportParams.value);
  } finally {
    loadingExcel.value = false;
  }
};

const getHostnameSafe = (): string => {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname;
  }
  return process.env.VITE_PUBLIC_HOST ?? process.env.SSR_HOST ?? '';
};
const IS_DOMINIOMEDIOS = getHostnameSafe() === 'crm.dominiomedios.com';
</script>

<style>
.v-overlay__content {
  backdrop-filter: blur(3px);
}
.apexcharts-toolbar{
  display: none!important;
}
</style>
